import { useState, useEffect } from "react";
import {
	Loader2,
	CornerDownRight,
	X,
	CircleDot,
	User,
	Shapes,
	FolderKanban,
	Rocket,
	Calendar,
	Clock,
	Tag as TagIcon,
} from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { cn } from "@/lib/utils";
import {
	type CreateTaskPayload,
	type ApiTaskStatus,
	type ApiTaskPriority,
} from "@/services/task.service";
import { type Project } from "@/services/project.service";
import { type Profile } from "@/services/profile.service";
import { listSprints, type Sprint } from "@/services/sprint.service";
import { listTaskTypes, type TaskType } from "@/services/task-type.service";
import { Icon, type IconName } from "@/components/ui/icon-picker";
import type { WorkflowStage } from "@/services/workflow-stage.service";
import { SYSTEM_STAGES } from "./system-stages";
import { StageChip, StageDot } from "./StageChip";
import {
	InlineTitle,
	InlineDescription,
	InlinePriority,
	InlineAssignee,
	InlineDueDate,
	InlineEstimatedTime,
	InlineTags,
	InlineSprint,
	InlineProject,
} from "./task-detail";

interface Props {
	open: boolean;
	onClose: () => void;
	onCreate: (projectId: string, payload: CreateTaskPayload) => Promise<void>;
	projects: Project[];
	profiles: Profile[];
	parentTaskId?: string;
	parentTitle?: string;
	lockedProjectId?: string;
	stages?: WorkflowStage[];
	filterProject?: string;
	escEnabled?: boolean;
	initialStatus?: string;
	initialProjectId?: string;
}

function MetaRow({
	icon,
	label,
	children,
}: {
	icon: React.ReactNode;
	label: string;
	children: React.ReactNode;
}) {
	return (
		<div className="flex items-start gap-3 py-2.5">
			<div className="flex w-24 shrink-0 items-center gap-1.5 pt-1.5 text-xs text-muted">
				{icon}
				{label}
			</div>
			<div className="min-w-0 flex-1 text-sm text-foreground">
				{children}
			</div>
		</div>
	);
}

export function NewTaskDrawerV2({
	open,
	onClose,
	onCreate,
	projects,
	profiles,
	parentTaskId,
	parentTitle,
	lockedProjectId,
	stages,
	filterProject,
	escEnabled = true,
	initialStatus,
	initialProjectId,
}: Props) {
	const [title, setTitle] = useState("");
	const [description, setDescription] = useState("");
	const [priority, setPriority] = useState<ApiTaskPriority>("medium");
	const [status, setStatus] = useState<ApiTaskStatus>("todo");
	const defaultProjectId =
		(filterProject && filterProject !== "all" ? filterProject : null) ??
		lockedProjectId ??
		projects[0]?.id ??
		"";
	const [projectId, setProjectId] = useState<string>(defaultProjectId);
	const [sprintId, setSprintId] = useState<string | null>(null);
	const [assigneeId, setAssigneeId] = useState<string | null>(null);
	const [assigneeIds, setAssigneeIds] = useState<string[]>([]);
	const [dueDate, setDueDate] = useState<string | null>(null);
	const [estimatedTime, setEstimatedTime] = useState<number>(0);
	const [tags, setTags] = useState<string[]>([]);
	const [sprints, setSprints] = useState<Sprint[]>([]);
	const [sprintsLoading, setSprintsLoading] = useState(false);
	const [taskTypes, setTaskTypes] = useState<TaskType[]>([]);
	const [taskTypeId, setTaskTypeId] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);

	const isSubtask = !!parentTaskId;

	useEffect(() => {
		if (!open) return;
		setTitle("");
		setDescription("");
		setPriority("medium");
		setStatus((initialStatus ?? "todo") as ApiTaskStatus);
		setProjectId(initialProjectId ?? defaultProjectId);
		setSprintId(null);
		setAssigneeId(null);
		setAssigneeIds([]);
		setDueDate(null);
		setEstimatedTime(0);
		setTags([]);
		setSubmitting(false);
	}, [
		open,
		lockedProjectId,
		filterProject,
		projects,
		initialStatus,
		initialProjectId,
	]);

	useEffect(() => {
		if (!open) return;
		let active = true;
		listTaskTypes()
			.then((data) => {
				if (!active) return;
				setTaskTypes(data);
				const def = data.find((t) => t.is_default) ?? data[0] ?? null;
				setTaskTypeId(def?.id ?? null);
			})
			.catch(() => {
				if (active) setTaskTypes([]);
			});
		return () => {
			active = false;
		};
	}, [open]);

	useEffect(() => {
		if (!open || !projectId) {
			setSprints([]);
			return;
		}
		let active = true;
		setSprintsLoading(true);
		listSprints()
			.then((data) => {
				if (!active) return;
				setSprints(data);
				const activeSprint = data.find((s) => s.status === "active");
				if (activeSprint) setSprintId(activeSprint.id);
			})
			.catch(() => {
				if (active) setSprints([]);
			})
			.finally(() => {
				if (active) setSprintsLoading(false);
			});
		return () => {
			active = false;
		};
	}, [open, projectId]);

	// Close on Escape when this is the topmost drawer.
	useEffect(() => {
		if (!open || !escEnabled) return;
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") onClose();
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [open, escEnabled, onClose]);

	const assignee = assigneeId
		? (profiles.find((p) => p.id === assigneeId) ?? null)
		: null;
	const additionalAssignees = profiles.filter((p) =>
		assigneeIds.includes(p.id),
	);
	const sprint = sprintId
		? (sprints.find((s) => s.id === sprintId) ?? null)
		: null;
	const stageList = stages ?? SYSTEM_STAGES;

	async function handleCreate() {
		if (!title.trim()) {
			toast.error("Title is required");
			return;
		}
		if (!projectId) {
			toast.error("Project is required");
			return;
		}
		const payload: CreateTaskPayload = {
			title: title.trim(),
			description: description || undefined,
			status,
			priority,
			assigned_to: assigneeId ?? undefined,
			assignee_ids: assigneeIds.length
				? assigneeIds.filter((id) => id !== assigneeId)
				: undefined,
			due_date: dueDate ?? undefined,
			tags: tags.length ? tags : undefined,
			project_id: projectId,
			sprint_id: sprintId ?? undefined,
			estimated_time: estimatedTime || undefined,
			parent_task_id: parentTaskId,
			task_type_id: taskTypeId ?? undefined,
		};
		setSubmitting(true);
		try {
			await onCreate(projectId, payload);
			onClose();
		} catch (e) {
			toast.error("Failed to create task", {
				description: (e as Error).message || "Please try again.",
			});
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<>
			<div
				className={cn(
					"fixed inset-0 z-[41] bg-black/40 backdrop-blur-sm transition-opacity",
					open ? "opacity-100" : "pointer-events-none opacity-0",
				)}
				onClick={onClose}
			/>
			<aside
				className={cn(
					"fixed right-0 top-0 z-[51] flex h-full w-full max-w-4xl flex-col bg-surface shadow-2xl transition-transform duration-300",
					open ? "translate-x-0" : "translate-x-full",
				)}
				aria-hidden={!open}
			>
				{open && (
					<>
						{/* Header */}
						<header className="flex items-center justify-between gap-3 border-b border-border bg-surface px-5 py-3">
							<div className="flex min-w-0 items-center gap-3">
								<InlinePriority
									value={priority}
									canEdit
									onSave={async (p) => {
										setPriority(p);
									}}
								/>
								<span className="text-sm text-muted">/</span>
								<span className="truncate text-sm font-medium text-primary">
									{isSubtask ? "NEW SUBTASK" : "NEW TASK"}
								</span>
							</div>
							<div className="flex shrink-0 items-center gap-1">
								{isSubtask && (
									<Badge variant="todo">Subtask</Badge>
								)}
								<button
									type="button"
									onClick={onClose}
									className="rounded-md p-1.5 text-muted hover:bg-muted-subtle hover:text-foreground"
									aria-label="Close"
								>
									<X className="h-4 w-4" />
								</button>
							</div>
						</header>

						{/* Body */}
						<div className="grid flex-1 grid-cols-1 overflow-y-auto md:grid-cols-[1fr_320px]">
							{/* Main column */}
							<div className="min-w-0 space-y-7 px-6 py-6">
								{isSubtask && parentTitle && (
									<span className="inline-flex items-center gap-1 text-xs text-primary">
										<CornerDownRight className="h-3 w-3" />
										Part of {parentTitle}
									</span>
								)}

								{/* Title + description */}
								<div>
									<InlineTitle
										value={title || "Untitled task"}
										canEdit
										onSave={async (v) => {
											setTitle(v);
										}}
									/>
									<InlineDescription
										value={description}
										canEdit
										onSave={async (v) => {
											setDescription(v);
										}}
									/>
								</div>

								<div className="rounded-lg border border-dashed border-border px-4 py-6 text-center">
									<p className="text-xs text-muted">
										Subtasks, attachments, notes, and
										comments can be added after the task is
										created.
									</p>
								</div>
							</div>

							{/* Meta rail */}
							<aside className="border-t border-border bg-muted-subtle/30 px-5 py-5 md:border-l md:border-t-0">
								<div className="divide-y divide-border">
									{/* Status */}
									<MetaRow
										icon={
											<CircleDot className="h-3.5 w-3.5" />
										}
										label="Status"
									>
										<SearchableSelect<{ color: string }>
											value={status}
											onValueChange={(v) =>
												setStatus(v as ApiTaskStatus)
											}
											size="sm"
											placeholder="Select status"
											options={stageList.map((stage) => ({
												value: stage.key,
												label: stage.name,
												meta: { color: stage.color },
											}))}
											renderOption={(opt) => (
												<StageChip
													label={opt.label}
													color={
														opt.meta?.color ?? ""
													}
												/>
											)}
											renderValue={(opt) => (
												<span
													className="inline-flex items-center gap-2 font-medium"
													style={{
														color: opt.meta?.color,
													}}
												>
													<StageDot
														color={
															opt.meta?.color ??
															""
														}
													/>
													{opt.label}
												</span>
											)}
										/>
									</MetaRow>

									{/* Assignees */}
									<MetaRow
										icon={<User className="h-3.5 w-3.5" />}
										label="Assignees"
									>
										<InlineAssignee
											assignee={
												assignee
													? {
															id: assignee.id,
															full_name:
																assignee.full_name,
															email: assignee.email,
															avatar_url:
																assignee.avatar_url,
														}
													: null
											}
											additionalAssignees={
												additionalAssignees
											}
											profiles={profiles}
											canEdit
											onSave={async (v) => {
												setAssigneeId(v);
												if (v) {
													setAssigneeIds((prev) =>
														prev.filter(
															(id) => id !== v,
														),
													);
												}
											}}
											onSaveAdditional={async (ids) => {
												setAssigneeIds(ids);
											}}
										/>
									</MetaRow>

									{/* Type */}
									<MetaRow
										icon={
											<Shapes className="h-3.5 w-3.5" />
										}
										label="Type"
									>
										<SearchableSelect<{
											color: string;
											icon: string;
										}>
											value={taskTypeId ?? ""}
											onValueChange={(v) =>
												setTaskTypeId(v || null)
											}
											disabled={taskTypes.length === 0}
											size="sm"
											placeholder="Select type"
											options={taskTypes.map((t) => ({
												value: t.id,
												label: t.name,
												meta: {
													color: t.color,
													icon: t.icon,
												},
											}))}
											renderOption={(opt) => (
												<span className="inline-flex items-center gap-2">
													<span
														className="inline-flex h-4 w-4 items-center justify-center rounded text-white"
														style={{
															background:
																opt.meta?.color,
														}}
													>
														<Icon
															name={
																opt.meta
																	?.icon as IconName
															}
															className="h-2.5 w-2.5"
														/>
													</span>
													<span className="text-base">
														{opt.label}
													</span>
												</span>
											)}
											renderValue={(opt) => (
												<span className="inline-flex items-center gap-2">
													<span
														className="inline-flex h-4 w-4 items-center justify-center rounded text-white"
														style={{
															background:
																opt.meta?.color,
														}}
													>
														<Icon
															name={
																opt.meta
																	?.icon as IconName
															}
															className="h-2.5 w-2.5"
														/>
													</span>
													{opt.label}
												</span>
											)}
										/>
									</MetaRow>

									{/* Project */}
									<MetaRow
										icon={
											<FolderKanban className="h-3.5 w-3.5" />
										}
										label="Project"
									>
										<InlineProject
											projectId={projectId}
											projects={projects}
											canEdit
											locked={!!lockedProjectId}
											onSave={async (v) => {
												setProjectId(
													v == "all" ? "" : v,
												);
												setSprintId(null);
											}}
										/>
									</MetaRow>

									{/* Sprint */}
									<MetaRow
										icon={
											<Rocket className="h-3.5 w-3.5" />
										}
										label="Sprint"
									>
										<InlineSprint
											sprint={
												sprint
													? {
															id: sprint.id,
															name: sprint.name,
															status: sprint.status,
															start_date:
																sprint.start_date,
															end_date:
																sprint.end_date,
														}
													: null
											}
											sprints={sprints}
											loading={sprintsLoading}
											canEdit
											onSave={async (v) => {
												setSprintId(v);
											}}
										/>
									</MetaRow>

									{/* Due date */}
									<MetaRow
										icon={
											<Calendar className="h-3.5 w-3.5" />
										}
										label="Due date"
									>
										<InlineDueDate
											value={dueDate}
											canEdit
											onSave={async (v) => {
												setDueDate(v);
											}}
										/>
									</MetaRow>

									{/* Estimate */}
									<MetaRow
										icon={<Clock className="h-3.5 w-3.5" />}
										label="Estimate"
									>
										<InlineEstimatedTime
											value={estimatedTime}
											canEdit
											onSave={async (v) => {
												setEstimatedTime(v);
											}}
										/>
									</MetaRow>

									{/* Tags */}
									<MetaRow
										icon={
											<TagIcon className="h-3.5 w-3.5" />
										}
										label="Tags"
									>
										<InlineTags
											value={tags}
											canEdit
											onSave={async (v) => {
												setTags(v);
											}}
										/>
									</MetaRow>
								</div>

								<div className="mt-5 flex flex-col gap-2">
									<Button
										onClick={handleCreate}
										disabled={submitting}
										className="w-full"
									>
										{submitting && (
											<Loader2 className="mr-2 h-4 w-4 animate-spin" />
										)}
										{isSubtask
											? "Create Subtask"
											: "Create Task"}
									</Button>
									<Button
										variant="outline"
										onClick={onClose}
										disabled={submitting}
										className="w-full"
									>
										Cancel
									</Button>
								</div>
							</aside>
						</div>
					</>
				)}
			</aside>
		</>
	);
}
